import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import { ArrowLeft, Trophy, Sparkles, Heart } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const PCT_DOMAIN = [0, 100];

export default function ParentDashboard() {
  const { userId, signOut } = useSession();
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [progress, setProgress] = useState({});
  const [badges, setBadges] = useState({});
  const [summaries, setSummaries] = useState({});
  const [activeChild, setActiveChild] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!userId) { navigate("/"); return; }
    api.getUser(userId).then(setParent);
    api.listStudents({ parent_id: userId }).then(async (list) => {
      setChildren(list);
      if (list[0]) setActiveChild(list[0].user.id);
      const allP = {}, allB = {}, allS = {};
      for (const c of list) {
        allP[c.user.id] = await api.listProgress(c.user.id);
        allB[c.user.id] = await api.listBadges(c.user.id);
        allS[c.user.id] = await api.progressSummary(c.user.id);
      }
      setProgress(allP); setBadges(allB); setSummaries(allS);
    });
  }, [userId]);

  const child = children.find(c => c.user.id === activeChild);
  const childProgress = progress[activeChild] || [];
  const childBadges = badges[activeChild] || [];
  const sum = summaries[activeChild] || {};

  const trend = [...childProgress].reverse().slice(-15).map((p, i) => ({ n: i + 1, accuracy: p.accuracy }));

  const generateAi = async () => {
    if (!activeChild) return;
    setAiLoading(true);
    try {
      const r = await api.aiSummary({ student_id: activeChild, audience: "parent" });
      setSummaries(s => ({ ...s, [activeChild]: { ...s[activeChild], ai: r.summary } }));
    } catch { toast.error("AI summary failed"); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between">
        <button onClick={() => { signOut(); navigate("/"); }} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="exit-parent">
          <ArrowLeft className="h-4 w-4" /> Exit
        </button>
        {parent && (
          <div className="flex items-center gap-3">
            <img src={parent.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div className="text-right">
              <div className="font-semibold text-sm">{parent.name}</div>
              <div className="text-xs text-muted-foreground">Parent</div>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Family progress</h1>
          <p className="text-muted-foreground mt-1">Celebrate every step your child takes.</p>
        </div>

        {children.length > 1 && (
          <div className="flex gap-2 flex-wrap" data-testid="child-tabs">
            {children.map(c => (
              <button
                key={c.user.id}
                onClick={() => setActiveChild(c.user.id)}
                data-testid={`child-tab-${c.user.id}`}
                className={`rounded-full px-5 py-2 font-medium border ${activeChild === c.user.id ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
              >
                {c.user.name}
              </button>
            ))}
          </div>
        )}

        {child && (
          <>
            <section className="flex items-center gap-5 bg-white p-6 rounded-xl border border-border">
              <img src={child.user.avatar} alt="" className="h-16 w-16 rounded-2xl object-cover bg-sky-100" />
              <div className="flex-1">
                <div className="text-xl font-bold">{child.user.name}</div>
                <div className="text-sm text-muted-foreground">Age {child.profile.age}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <Mini label="Sessions" value={sum.total_sessions || 0} />
                <Mini label="Avg %" value={sum.average_accuracy || 0} />
                <Mini label="Stars" value={sum.total_stars || 0} />
              </div>
            </section>

            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="p-5 bg-white">
                <div className="font-semibold mb-2">Accuracy over recent sessions</div>
                <div className="h-56" data-testid="parent-accuracy-chart">
                  <ResponsiveContainer>
                    <LineChart data={trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="n" />
                      <YAxis domain={PCT_DOMAIN} />
                      <Tooltip />
                      <Line type="monotone" dataKey="accuracy" stroke="#10B981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
              <Card className="p-5 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-orange-500" /> Weekly summary</div>
                  <Button size="sm" variant="outline" onClick={generateAi} disabled={aiLoading} data-testid="parent-gen-ai">
                    {aiLoading ? "Generating…" : "Generate"}
                  </Button>
                </div>
                <div className="text-sm whitespace-pre-wrap min-h-[120px]" data-testid="parent-ai-summary">
                  {sum.ai || "Tap Generate to see a friendly summary of your child's progress."}
                </div>
              </Card>
            </div>

            <Card className="p-5 bg-white">
              <div className="font-semibold mb-4 flex items-center gap-2"><Trophy className="h-5 w-5 text-orange-500" /> Achievements ({childBadges.length})</div>
              {childBadges.length === 0 ? (
                <div className="text-sm text-muted-foreground">No achievements yet — your child will earn their first soon!</div>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="parent-badges">
                  {childBadges.map(b => (
                    <div key={b.id} className="border border-orange-200 bg-orange-50/40 rounded-xl p-4">
                      <div className="flex items-center gap-2 font-bold">
                        <Heart className="h-4 w-4 text-orange-500 fill-orange-500" /> {b.name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">{b.description}</div>
                      <div className="text-[10px] text-muted-foreground mt-2">{new Date(b.earned_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5 bg-white">
              <div className="font-semibold mb-3">Recent activity</div>
              {childProgress.length === 0 ? (
                <div className="text-sm text-muted-foreground">Your child hasn&apos;t started yet. Encourage them to give it a go!</div>
              ) : (
                <ul className="divide-y">
                  {childProgress.slice(0, 8).map(p => (
                    <li key={p.id} className="py-2 flex items-center justify-between text-sm">
                      <span>{p.exercise_title}</span>
                      <span className="text-muted-foreground">{p.stars}★ · {p.accuracy}%</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg py-2 px-3 min-w-[64px]">
      <div className="font-bold text-lg">{value}</div>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
    </div>
  );
}
